import { defineStore } from 'pinia'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import router from '@/router'
import { WebViewLayout } from '@/plugins/webViewLayout'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null
        , initialized: false
        , isLoggingOut: false
    }),

    getters: {
        isAuthenticated: (state) => !!state.user
    },

    actions: {
        async initCsrf() {
            await axios.get('/front/api/csrf', { withCredentials: true });
        },

        // 同一ブラウザの別タブへログイン、ログアウトを通知する
        notifyAuthChangedToOtherTabs() {
            localStorage.setItem('auth:changed', Date.now().toString());
        },
        // 認証ユーザー取得
        async checkAuth() {
            try {
                // 未ログイン時の401は想定内の応答のため、
                // ログイン画面への遷移は行わず呼び出し元で判定する
                const res = await axios.get('/front/api/me', {
                    withCredentials: true
                    , skipAuthRedirect: true
                });

                this.user = res.data || null;
                return !!res.data;
            } catch(e) {
                if (e.response?.status === 401) {
                    this.user = null;
                    return false;
                }
                throw e;
            } 
        },

        // ユーザー情報取得
        async fetchUserData() {
            try {
                const res = await axios.get('/front/api/user-data', { withCredentials: true });
                const data = res.data.response || res.data;
                this.user = {
                    ...data,
                    formalName1: data.formalName1 ?? '',
                    formalName2: data.formalName2 ?? ''
                };
            } catch(error) {
                this.user = null;
                throw error;
            }
        },

        async fetchMe() {
            try {
                const isAuthenticated = await this.checkAuth();

                if(!isAuthenticated) {
                    this.user = null;
                    return;
                }

                await this.fetchUserData();
            } catch(error) {
                if (error.response?.status === 401) {
                    this.user = null;
                    return;
                }
                this.user = null;
                throw error;
            } finally {
                this.initialized = true;
            }
        },

        async login(payload) {
            try {
                const resAuth = await axios.post('/front/api/login', payload, { withCredentials: true })

                // ログインでセッションが確立した直後に CSRF を再取得（POST と整合させる）
                await this.initCsrf();

                // SpringSecurityのSuccessHandlerから返されたユーザー情報を保存
                if (resAuth.data.success && resAuth.data.user) {
                    await this.fetchUserData();
                    this.initialized = true;
                } else if(resAuth.data.response) {
                    this.user = resAuth.data.response;
                    this.initialized = true;
                } else {
                    // 認証がクリアされた場合 /api/meを呼び再取得
                    await this.fetchMe();
                }

                // Android: セッション Cookie を即時永続化（タスクキル前にディスクへ書く）
                await this.flushSessionCookies();

                this.notifyAuthChangedToOtherTabs();
            } catch(e) {
                const res = e.response;

                if (res?.status === 403 && res.data?.code === 'FORCED_PASSWORD_CHANGE') {
                    // トークン情報をsessionStorageに保存
                    if (res.data.token) {
                        sessionStorage.setItem(
                            'FORCED_PASSWORD_CHANGE_TOKEN'
                            , JSON.stringify({
                                token: res.data.token
                                , customerUnityCode: res.data.customerUnityCode
                                , isKeepLoggedIn: res.data.isKeepLoggedIn
                                , isForcedChangeEmail: res.data.isForcedChangeEmail
                            })
                        );
                    }
                    return {
                        status: 'FORCED_PASSWORD_CHANGE'
                    }
                }
                throw e;
            }    
        },

        async logout() {
            // 二重実行防止
            if (this.isLoggingOut) return;
            this.isLoggingOut = true;
            try {
                await axios.post('/front/api/logout', {}, {
                    withCredentials: true
                })
            } finally {
                // 認証状態を完全リセット
                this.user = null;
                this.initialized = false;
                this.isLoggingOut = false;
                await this.flushSessionCookies();
                this.notifyAuthChangedToOtherTabs();
                router.replace('/login');
            }
        },

        async flushSessionCookies() {
            if (Capacitor.getPlatform() !== 'android') return;
            try {
                await WebViewLayout.flushCookies();
            } catch (_) {
                // プラグイン未同期・Web 実行時は無視
            }
        }
    }
})